import { ajax } from 'rxjs/ajax';
import { map, mapTo } from 'rxjs/operators';
import User from 'Js/api/v1/model/User';
import Image from 'Js/api/v1/model/Image';
import Reply from './Reply';

export default class Topic {
    constructor({
        id,
        url,
        name,
        author,
        isSubscribed,
        publicationDate,
        lastUpdateDate,
        body,
        bodyHtml,
        files,
        isPinned,
        isActive,
        isDeleted,
        isSealed,
        isHidden,
        childrenCount,
        lastChild
    }) {
        /**
         * Идентификатор темы
         * @type {number}
         */
        this.id = id;
        /**
         * Ссылка на тему (от корня форума)
         * @type {string}
         */
        this.url = url;
        /**
         * Заголовок темы
         * @type {string}
         */
        this.name = name;
        /**
         * Автор темы
         * @type {User}
         */
        this.author = author;
        /**
         * Дата публикации темы
         * @type {Date}
         */
        this.publicationDate = publicationDate;
        /**
         * Дата последнего изменения в теме
         * (равна publicationDate, если тема не редактировалась)
         * @type {Date}
         */
        this.lastUpdateDate = lastUpdateDate || publicationDate;
        /**
         * Основной текст
         * @type {string}
         */
        this.body = body;
        /**
         * Основной текст с разметкой HTML
         * (не связан с body, заполняется отдельно и только привилегированным пользователем)
         * @type {string}
         */
        this.bodyHtml = bodyHtml;
        /**
         * Прикреплённые к теме файлы
         * (могут быть разных типов, но парсятся как Image, чтобы сформировать превью)
         * @type {Image[]}
         */
        this.files = files.sort((f1, f2) => f1.id - f2.id);
        /**
         * Тема закреплена в треде
         * @type {boolean}
         */
        this.isPinned = isPinned;
        /**
         * Тема активна, то есть выводится в списке тем
         * @type {boolean}
         */
        this.isActive = isActive;
        /**
         * Тема удалена, то есть не активна и прямые ссылки на неё не работают
         * @type {boolean}
         */
        this.isDeleted = isDeleted;
        /**
         * Подписан ли текущий пользователь на уведомления о событиях в теме
         * @type {boolean}
         */
        this.isSubscribed = isSubscribed;
        /**
         * Тема закрыта администратором
         * @type {boolean}
         */
        this.isSealed = isSealed;
        /**
         * Тема скрыта из списка последних тем
         * @type {boolean}
         */
        this.isHidden = isHidden;
        /**
         * Количество ответов в теме
         * @type {number}
         */
        this.childrenCount = childrenCount;
        /**
         * Последний ответ в теме
         * @type {Reply}
         */
        this.lastChild = lastChild;
    }

    static parse(DTO) {
        if (DTO && typeof DTO == 'object') {
            const {
                Id,
                Url,
                Name,
                Author,
                AmISubscribed,
                PublicationDate,
                ModifiedDate,
                Body,
                BodyHtml,
                Files,
                IsPinned,
                IsActive,
                IsDeleted,
                Sealed,
                Exclude,
                RepliesCount,
                LastReply
            } = DTO;

            return new Topic({
                id: Id,
                url: Url,
                name: Name,
                author: User.parse(Author),
                isSubscribed: AmISubscribed,
                publicationDate: new Date(PublicationDate),
                lastUpdateDate: ModifiedDate ? new Date(ModifiedDate) : null,
                body: Body,
                bodyHtml: BodyHtml,
                files: Files.map(Image.parse),
                isPinned: IsPinned,
                isActive: IsActive,
                isDeleted: IsDeleted,
                isSealed: Sealed,
                isHidden: Exclude,
                childrenCount: RepliesCount,
                lastChild: Reply.parse(LastReply)
            });
        }
        return null;
    }

    /**
     * Создаёт новый экземпляр Topic,
     * заполненный данными из исходного объекта
     * @param {Object} topic
     * @returns {Topic}
     */
    static create(topic) {
        const topicDefaults = {
            name: '',
            body: '',
            bodyHtml: '',
            files: [],
            childrenCount: 0,
            isPinned: false,
            isActive: true,
            isDeleted: false,
            isSealed: false,
            isHidden: false
        };
        return new Topic({ ...topicDefaults, ...topic });
    }

    /**
     * Загружает данные по теме
     * @param {number} topicId
     * @returns {Observable}
     */
    static get(topicId) {
        return ajax
            .getJSON(`/api/v1/forum/topics/${topicId}`)
            .pipe(map(topic => Topic.parse(topic)));
    }

    /**
     * Загружает список последних добавленных тем,
     * или тем, в которые были добавлены сообщения
     * @param {Object} param0
     * @param {number} [param0.offset]
     * @param {number} [param0.limit]
     * @returns {Observable}
     */
    static getLastTopics({ offset = 0, limit = 20 }) {
        return ajax
            .get(`/api/v1/forum/topics?offset=${offset}&limit=${limit}`)
            .pipe(
                map(({ xhr, response: results }) => ({
                    results: results.map(Topic.parse),
                    offset,
                    limit,
                    totalCount: parseInt(
                        xhr.getResponseHeader('X-Total-Count'),
                        10
                    )
                }))
            );
    }

    /**
     * Загружает список тем в разделе
     * @param {number} threadId
     * @param {Object} param1
     * @param {number} [param1.offset]
     * @param {number} [param1.limit]
     * @returns {Observable}
     */
    static getThread(threadId, { offset = 0, limit = 30 }) {
        return ajax
            .get(
                `/api/v1/forum/threads/${threadId}/topics?offset=${offset}&limit=${limit}`
            )
            .pipe(
                map(({ xhr, response: results }) => ({
                    results: results.map(Topic.parse),
                    offset,
                    limit,
                    totalCount: parseInt(
                        xhr.getResponseHeader('X-Total-Count'),
                        10
                    )
                }))
            );
    }

    /**
     * Загружает список тем, в которых пользователь оставил сообщение
     * Сообщение записано в lastReply темы
     * (даже если по факту оно не являтся последним)
     * @param {Object} param0
     * @param {number} [param0.offset]
     * @param {number} [param0.limit]
     * @returns {Observable}
     */
    static getUserRepliedTopics(userId, { offset = 0, limit = 30 }) {
        return ajax
            .get(
                `/api/v1/forum/users/${userId}/replies?offset=${offset}&limit=${limit}`
            )
            .pipe(
                map(({ xhr, response: results }) => ({
                    results: results.map(Topic.parse),
                    offset,
                    limit,
                    totalCount: parseInt(
                        xhr.getResponseHeader('X-Total-Count'),
                        10
                    )
                }))
            );
    }

    /**
     * Удаляет тему
     * @param {number} topicId
     * @returns {Observable}
     */
    static delete(topicId) {
        return ajax
            .delete(`/api/v1/forum/topics/${topicId}`)
            .pipe(mapTo(topicId));
    }

    /**
     * Ability configuration
     * @see https://casl.js.org/v5/en/guide/subject-type-detection
     */
    static get modelName() {
        return 'Topic';
    }

    /**
     * Создаёт тему в разделе
     * @param {number} threadId
     * @returns {Observable}
     */
    post(threadId) {
        const data = new FormData();

        data.append('topic', JSON.stringify(this));

        this.files.forEach(({ blob }) => {
            if (blob) {
                data.append('files', blob);
            }
        });

        return ajax
            .post(`/api/v1/forum/threads/${threadId}/topics`, data)
            .pipe(map(({ response: topic }) => Topic.parse(topic)));
    }

    /**
     * Редактирует тему
     * @param {File[]} delFiles - удаляемые файлы
     * @returns {Observable}
     */
    patch(delFiles = []) {
        const data = new FormData();
        const delFilesIds = delFiles.map(f => f.id).filter(v => v);

        data.append('topic', JSON.stringify(this));

        this.files.forEach(({ blob }) => {
            if (blob) {
                data.append('newFiles', blob);
            }
        });

        return ajax
            .patch(
                delFilesIds.length > 0
                    ? `/api/v1/forum/topics/${this.id}?delFiles=${delFilesIds}`
                    : `/api/v1/forum/topics/${this.id}`,
                data
            )
            .pipe(map(({ response: topic }) => Topic.parse(topic)));
    }

    /**
     * Убирает тему из списка "новых" тем на форуме
     * @returns {Observable}
     */
    hide() {
        return ajax.post(`/api/v1/forum/topics/${this.id}/hide`).pipe(
            mapTo(
                Topic.create({
                    ...this,
                    isHidden: true
                })
            )
        );
    }

    /**
     * Подписывает текущего пользователя на рассылку по изменениям в теме
     * @returns {Observable}
     */
    subscribe() {
        return ajax.post(`/api/v1/forum/topics/${this.id}/subscription`).pipe(
            mapTo(
                Topic.create({
                    ...this,
                    isSubscribed: true
                })
            )
        );
    }

    /**
     * Отписывает текущего пользователя от рассылки по изменениям в теме
     * @returns {Observable}
     */
    unsubscribe() {
        return ajax.delete(`/api/v1/forum/topics/${this.id}/subscription`).pipe(
            mapTo(
                Topic.create({
                    ...this,
                    isSubscribed: false
                })
            )
        );
    }

    /**
     * Возвращает всех подписчиков темы
     * @returns {Observable}
     */
    getSubscribers() {
        return ajax
            .delete(`/api/v1/forum/topics/${this.id}/subscribers`)
            .pipe(map(({ response: users }) => users.map(User.parse)));
    }

    /**
     * Удаляет конкретный файл из прикреплённых к теме
     * @param {File} file
     * @returns {Observable}
     */
    deleteFile(file) {
        return ajax
            .delete(`/api/v1/forum/topics/${this.id}/files/${file.id}`)
            .pipe(
                mapTo(
                    Topic.create({
                        ...this,
                        files: this.files.filter(f => f.id != file.id)
                    })
                )
            );
    }

    toJSON() {
        return {
            Title: this.name,
            BBCodeMessage: this.body,
            HtmlMessage: this.bodyHtml,
            IsPinned: this.isPinned,
            Sealed: this.isSealed,
            StaticDate: this.publicationDate,
            /**
             * Необходимо передавать CreatedBy, чтобы при редактировании
             * темы автор не обнулялся.
             * @todo исправить на бекенде и избавиться от параметра CreatedBy.
             */
            CreatedBy: this.author ? this.author.id : undefined
            /**
             * @todo не мешало бы добавить поле IsHidden.
             * IsHidden: this.isHidden
             */
        };
    }

    toString() {
        return this.name;
    }
}
